import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { EnvBadge } from "../components/Layout";
import { Button, Field, InlineError, TextInput } from "../components/ui";
import { API_BASE, API_MODE } from "../env";
import styles from "./pages.module.css";

export function LoginPage() {
  const { admin, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const next = params.get("next") || "/";

  if (admin) return <Navigate to={next} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      navigate(next, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? new ApiError(429, "rate_limited", "Too many attempts. Wait a minute and try again.")
          : err,
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.login}>
      <div className={`${styles.loginGlow} screen-azure`} aria-hidden />
      <form className={styles.loginCard} onSubmit={submit} noValidate>
        <div className={styles.loginBrand}>
          <span className={styles.loginDot} aria-hidden />
          <span className="monoLabel">IQ Daily · admin</span>
          <EnvBadge />
        </div>
        <h1 className={styles.loginTitle}>
          Sign in to
          <br />
          <span className={styles.loginAccent}>the control room.</span>
        </h1>
        <p className={styles.loginSub}>Content review, schedule, levels and integrity for the daily puzzle.</p>

        <Field label="Email">
          <TextInput
            type="email"
            autoComplete="username"
            placeholder="you@iqdaily.app"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            required
          />
        </Field>
        <Field label="Password">
          <TextInput
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </Field>
        <InlineError error={error} />
        <Button type="submit" variant="primary" busy={busy} disabled={!email || !password} className={styles.loginSubmit}>
          Sign in
        </Button>

        <div className={styles.loginNote}>
          {API_MODE === "mock" ? (
            <>
              <span className="monoLabel">Mock mode</span>
              <span>
                Any email with password <code>lumen-dev</code>. The role comes from the address:{" "}
                <code>viewer@…</code>, <code>editor@…</code>, anything else is <code>admin</code>.
              </span>
            </>
          ) : (
            <>
              <span className="monoLabel">Live</span>
              <span>
                Cookie session against <code>{API_BASE}/admin/v1</code>. Accounts are created with{" "}
                <code>pnpm admin:create</code>.
              </span>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
